/**
 * SatelliteStrip — Section 4 of /map redesign spec.
 *
 * V2 refactor (Sprint 2026-21, Task 9): migrated to single useMapStoreV2,
 * TOGGLE_RASTER_MODE toggle semantics, ASC date order, empty product
 * disabled pills, and CLOSE_SAT_PANEL mute-all collapse.
 *
 * Spec: docs/superpowers/specs/2026-05-07-map-state-machine.md
 *   §3.2 TOGGLE_RASTER_MODE — click on active product = off (founder lock-in #1)
 *   §3.4 selectedDate lifecycle — preserved across product switches
 *   §9.1 Empty states — `availableDates(product)=∅` → pill disabled + tooltip
 *
 * Visual layout (unchanged from v2.4 prototype):
 *
 *   Row 1 (products + actions):
 *     [⋮⋮ drag] [NDVI] [NDVI сред.] [NDVI контр.] [GNR] [Однородн.]
 *                                       ... spacer ...
 *               [⇄ compare] [☁ cloud] [📷 RGB] [⚙ calibration]
 *               [|] [− collapse]
 *
 *   Row 2 (year + months + days, dates ASC oldest → newest):
 *     [← 2025]  |  [янв][фев]…[дек]  |  [1][3][6]…[24]
 *
 * Founder bug B (Sprint 2026-19): days were rendered DESC. Now ASC strictly —
 * tree builder sorts month days ascending and the day list iterates in that
 * order. Verified by `sat-strip-v2.test.tsx`.
 *
 * No prop API: component reads from V2 store directly. Mount gate in v2.tsx
 * is also driven by V2 store's `satelliteStripOpen`.
 *
 * Available-dates fetching is owned by `useDatesEffect` (Effect 1, T4) —
 * this component is a pure read-side renderer plus dispatch-only writer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GripVertical,
  ArrowLeftRight,
  Cloud,
  Camera,
  Settings,
  Minus,
  ChevronLeft,
  Loader2,
  X,
} from 'lucide-react';
import { useMapStore } from '@/lib/map/useMapStoreV2';
import { productOf } from '@/lib/map/derived/productOf';
import type { RasterMode, AvailableKey, RegionWarmStatus } from '@/lib/map/state/mapState';
import { availableKey } from '@/lib/map/derived/availableKey';
import { useActiveSeason } from '@/lib/season/SeasonContext';

/**
 * Product pills — NO 'none' option here. Per v2.4: turning off all products
 * is done via the collapse [−] button on the right side OR by toggling the
 * currently-active product (TOGGLE_RASTER_MODE same-mode → 'none').
 */
const PRODUCTS: Array<{ value: Exclude<RasterMode, 'none'>; label: string }> = [
  { value: 'ndvi', label: 'NDVI' },
  { value: 'avg_ndvi', label: 'NDVI сред.' },
  { value: 'ndvi_contrast', label: 'NDVI контр.' },
  { value: 'gnr', label: 'GNR' },
  { value: 'uniformity', label: 'Однородн.' },
];

const MONTHS_RU = [
  'янв', 'фев', 'мар', 'апр', 'май', 'июн',
  'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
];

/**
 * AK-604-follow (founder 16.08) — honest copy for an empty dates list.
 * Agronomist language only: no MGRS, COG, pipeline, tile, scene_rasters.
 * Three distinct causes the founder asked to tell apart, plus a fallback for
 * the one case we genuinely cannot classify with confidence server-side
 * ("stalled" — see region_warm_status.py for why we don't claim "territory
 * not covered", that would be invented certainty).
 */
function emptyDatesMessage(status: RegionWarmStatus | null): string {
  switch (status?.kind) {
    case 'loading':
      return status.etaMinutes
        ? `снимки ещё загружаются — обычно занимает около ${status.etaMinutes} минут`
        : 'снимки ещё загружаются';
    case 'no_recent_scenes':
      return 'за эти дни чистых снимков нет (облачно)';
    case 'stalled':
      return 'снимки не появляются дольше обычного — сообщите в поддержку';
    case 'unknown':
    default:
      // datesLoaded=true but no status: either dates were non-empty (this
      // branch isn't reached) or the backend didn't send one (older build /
      // classification failed fail-soft) — same honest fallback as before.
      return 'снимков нет';
  }
}

/** Group available dates into year → month → days[] (days ASC). */
interface DateTree {
  year: number | null;
  months: Map<number, number[]>; // month-index (0..11) → ASC day numbers
}

function buildDateTree(dates: string[]): DateTree {
  if (dates.length === 0) return { year: null, months: new Map() };
  // dates already sorted ASC by the caller; pick the most recent year present.
  const last = dates[dates.length - 1];
  const year = Number(last.slice(0, 4));
  const months = new Map<number, number[]>();
  for (const d of dates) {
    if (!d.startsWith(`${year}-`)) continue;
    const m = Number(d.slice(5, 7)) - 1;
    const day = Number(d.slice(8, 10));
    const arr = months.get(m) ?? [];
    arr.push(day);
    months.set(m, arr);
  }
  // Founder bug B: explicit ASC sort — guards against caller passing
  // unsorted input (defence in depth; current callers already sort).
  for (const arr of months.values()) arr.sort((a, b) => a - b);
  return { year, months };
}

function ymd(year: number, monthIdx: number, day: number): string {
  const mm = String(monthIdx + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

// ── AK-319: drag support for the strip ──────────────────────────────────────
// Mirror of the MapVerticalLegend draggable pattern (founder lock-in #5) but
// with the SatellitesPanel null→default-jump guard: the strip starts at its
// CSS bottom-center anchor (position === null); on first drag we seed origin
// from the live bounding box so it doesn't snap to (0,0).

/** Below this width drag is disabled (panels stack — matches DraggablePanel). */
const MOBILE_BREAKPOINT = 768;

/** Conservative strip footprint for viewport clamp (keeps grip reachable). */
const STRIP_MIN_W = 320;
const STRIP_MIN_H = 56;

function clampToViewport(x: number, y: number): { x: number; y: number } {
  if (typeof window === 'undefined') return { x, y };
  const maxX = Math.max(0, window.innerWidth - STRIP_MIN_W);
  const maxY = Math.max(0, window.innerHeight - STRIP_MIN_H);
  return {
    x: Math.max(0, Math.min(maxX, x)),
    y: Math.max(0, Math.min(maxY, y)),
  };
}

export interface SatelliteStripProps {
  /**
   * AK-383 #8: true while the raster tiles for the currently-selected date are
   * still loading. Owned by v2.tsx (which holds the MapLibre instance) via
   * `useRasterTilesLoading` — the strip just renders the spinner so the user
   * gets immediate feedback that the date switch registered.
   */
  tilesLoading?: boolean;
}

export function SatelliteStrip({ tilesLoading = false }: SatelliteStripProps = {}) {
  // Per-field selectors — avoids new-object-every-render that useShallow
  // partly mitigated. Each subscription fires only when its slice changes.
  const activeRasterMode = useMapStore((s) => s.activeRasterMode);
  const selectedDate = useMapStore((s) => s.selectedDate);
  const activeScope = useMapStore((s) => s.activeScope);
  const rgbSceneOn = useMapStore((s) => s.rgbSceneOn);
  const cloudMaskOn = useMapStore((s) => s.cloudMaskOn);
  const availableDates = useMapStore((s) => s.availableDates);
  const stripPosition = useMapStore((s) => s.satelliteStripPosition);
  const dispatch = useMapStore((s) => s.dispatch);

  // AK-195: active season is part of the availableDates cache key (must match
  // the writer in useDatesEffect). `season` is null outside SeasonProvider.
  const { season } = useActiveSeason();
  const seasonId = season?.seasonId ?? null;

  // AK-106 Task 2 (founder lock-in 2026-05-08): satellite products work only
  // when a `farm` scope is selected. For any other scope.type (company,
  // cluster, holding, brigade, array, field, tenant) hide the strip entirely.
  // Defense in depth: v2.tsx mount gate ALSO reads satelliteStripOpen which
  // the reducer (SET_ACTIVE_SCOPE non-farm) flips to false. This early-return
  // covers the edge case where dispatchScopeChanged hasn't yet fired but the
  // hydrate dispatch has.
  if (activeScope.type !== 'farm') return null;

  // Locally tracked active month — auto-syncs к месяцу выбранной даты, но
  // юзер может кликнуть другой месяц и увидеть его дни без выбора даты.
  const [activeMonth, setActiveMonth] = useState<number | null>(null);

  // ── AK-319 drag — make the grip's "Перетащить панель" tooltip true. ──────
  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);

  const onDragPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const { x, y } = clampToViewport(
        dragRef.current.origX + dx,
        dragRef.current.origY + dy,
      );
      dispatch({ type: 'SET_SATELLITE_STRIP_POSITION', x, y });
    },
    [dispatch],
  );

  const onDragPointerUp = useCallback(() => {
    setDragging(false);
    dragRef.current = null;
    window.removeEventListener('pointermove', onDragPointerMove);
    window.removeEventListener('pointerup', onDragPointerUp);
    window.removeEventListener('pointercancel', onDragPointerUp);
  }, [onDragPointerMove]);

  const onGripPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Mobile: drag off (panels stack) — parity with DraggablePanel/SatellitesPanel.
      if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT) {
        return;
      }
      e.preventDefault();
      // Seed origin: if not yet positioned (CSS bottom-center anchor), read the
      // live box so the strip doesn't jump to (0,0) on first drag.
      let origX = stripPosition?.x ?? 0;
      let origY = stripPosition?.y ?? 0;
      if (stripPosition == null && rootRef.current) {
        const box = rootRef.current.getBoundingClientRect();
        origX = box.left;
        origY = box.top;
      }
      dragRef.current = { startX: e.clientX, startY: e.clientY, origX, origY };
      setDragging(true);
      window.addEventListener('pointermove', onDragPointerMove);
      window.addEventListener('pointerup', onDragPointerUp);
      window.addEventListener('pointercancel', onDragPointerUp);
    },
    [stripPosition, onDragPointerMove, onDragPointerUp],
  );

  // Cleanup if unmounted mid-drag.
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onDragPointerMove);
      window.removeEventListener('pointerup', onDragPointerUp);
      window.removeEventListener('pointercancel', onDragPointerUp);
    };
  }, [onDragPointerMove, onDragPointerUp]);

  // Dates for the *currently active* product — derived from V2 store cache.
  // useDatesEffect (Effect 1, T4) is responsible for populating it. Here we
  // just read.
  const activeProduct = productOf(activeRasterMode);
  const dates = useMemo<string[]>(() => {
    if (!activeProduct) return [];
    const key: AvailableKey = availableKey(activeScope.type, activeScope.id, activeProduct, seasonId);
    const set = availableDates.get(key);
    if (!set) return [];
    // ASC sort — ISO date strings sort lexicographically = chronological.
    // Founder bug B: oldest date left, newest right.
    return Array.from(set).sort();
  }, [activeProduct, activeScope.type, activeScope.id, availableDates, seasonId]);

  // Whether the dates fetch has settled for the current (scope, product).
  // Distinct from `dates.length === 0`: if the key isn't in the cache yet,
  // we're loading; if it IS in the cache but the Set is empty, we're empty.
  const datesLoaded = useMemo<boolean>(() => {
    if (!activeProduct) return false;
    const key: AvailableKey = availableKey(activeScope.type, activeScope.id, activeProduct, seasonId);
    return availableDates.has(key);
  }, [activeProduct, activeScope.type, activeScope.id, availableDates, seasonId]);

  // AK-604-follow (founder 16.08): WHY the dates list is empty — set by
  // useDatesEffect from the backend's region_status. Only meaningful once
  // datesLoaded is true (see reducer: cleared on every fresh fetch success,
  // set only alongside an empty Set).
  const availableDatesStatus = useMapStore((s) => s.availableDatesStatus);
  const emptyDatesReason = useMemo(() => {
    if (!activeProduct) return null;
    const key: AvailableKey = availableKey(activeScope.type, activeScope.id, activeProduct, seasonId);
    return availableDatesStatus.get(key) ?? null;
  }, [activeProduct, activeScope.type, activeScope.id, availableDatesStatus, seasonId]);

  const tree = useMemo(() => buildDateTree(dates), [dates]);

  // Sync activeMonth to selectedDate's month or fall back to latest month
  // available in the tree.
  useEffect(() => {
    if (selectedDate) {
      setActiveMonth(Number(selectedDate.slice(5, 7)) - 1);
      return;
    }
    if (tree.months.size > 0) {
      const months = Array.from(tree.months.keys()).sort((a, b) => a - b);
      // ASC sort → take last for "latest available month".
      setActiveMonth(months[months.length - 1]);
    }
  }, [selectedDate, tree]);

  // Empty-state lookup per product pill (founder lock-in §9.1, row 3).
  // Only meaningful when dates have been fetched (key present in Map). If
  // not yet fetched, we don't know — render as enabled.
  const isProductEmpty = (mode: Exclude<RasterMode, 'none'>): boolean => {
    const product = productOf(mode);
    if (!product) return false;
    const key: AvailableKey = availableKey(activeScope.type, activeScope.id, product, seasonId);
    const set = availableDates.get(key);
    return set !== undefined && set.size === 0;
  };

  // Founder lock-in #2 — CLOSE_SAT_PANEL is the canonical mute-all action.
  // Reducer clears rasterMode, RGB, cloud, selectedDate, satelliteStripOpen
  // in one transition. No need for separate dispatches.
  const collapse = () => {
    dispatch({ type: 'CLOSE_SAT_PANEL' });
  };

  const sortedMonths = Array.from(tree.months.keys()).sort((a, b) => a - b);
  const visibleDays = activeMonth != null ? tree.months.get(activeMonth) ?? [] : [];

  // AK-319: once dragged, switch from the bottom-center CSS anchor to absolute
  // left/top px coords. Until then keep the original centered layout intact.
  const positioned = stripPosition != null;
  const containerStyle: React.CSSProperties = positioned
    ? {
        position: 'absolute',
        left: stripPosition.x,
        top: stripPosition.y,
        zIndex: 30,
        width: 920,
        maxWidth: 'calc(100% - 24px)',
      }
    : {
        position: 'absolute',
        bottom: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 30,
        width: 920,
        maxWidth: 'calc(100% - 24px)',
      };

  return (
    <div
      ref={rootRef}
      data-testid="map-satellite-strip"
      style={containerStyle}
      className={
        'rounded-lg border border-border bg-background/95 backdrop-blur-sm shadow-xl select-none ' +
        (dragging ? 'shadow-2xl' : '')
      }
    >
      {/* Row 1: drag handle + products + spacer + actions + separator + collapse */}
      <div className="flex items-center gap-1 overflow-x-auto px-1 py-2 border-b border-border">
        {/* AK-319: real drag handle — pointer-events drag, persisted position,
            disabled on mobile (<768, panels stack). Mirrors MapVerticalLegend
            drag pattern. */}
        <button
          type="button"
          aria-label="Перетащить панель"
          title="Перетащить панель"
          data-testid="sat-strip-drag-handle"
          onPointerDown={onGripPointerDown}
          className={
            'w-6 h-7 rounded hover:bg-accent flex items-center justify-center text-muted-foreground shrink-0 touch-none ' +
            (dragging ? 'cursor-grabbing' : 'cursor-grab')
          }
        >
          <GripVertical size={14} aria-hidden />
        </button>

        {/* Product pills — TOGGLE semantics (click on active = off). */}
        <div role="radiogroup" aria-label="Слой растра" className="inline-flex items-center gap-1">
          {PRODUCTS.map((p) => {
            const active = activeRasterMode === p.value;
            const empty = isProductEmpty(p.value);
            return (
              <button
                key={p.value}
                type="button"
                role="radio"
                aria-checked={active}
                aria-disabled={empty || undefined}
                disabled={empty}
                title={empty ? `Нет данных для ${p.label} в текущем scope` : undefined}
                data-testid={`sat-strip-raster-${p.value}`}
                data-empty={empty || undefined}
                onClick={() => {
                  if (empty) return;
                  dispatch({ type: 'TOGGLE_RASTER_MODE', mode: p.value });
                }}
                className={
                  'px-3 py-1 rounded text-xs font-medium border whitespace-nowrap transition-colors ' +
                  (empty
                    ? 'bg-background text-muted-foreground border-input opacity-50 cursor-not-allowed'
                    : active
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-foreground border-input hover:bg-accent')
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1" />

        {/* Action icons — compare (stub), cloud (toggle), snapshot/RGB
            (toggle), calibration (stub). */}
        <button
          type="button"
          aria-label="Сравнить даты"
          title="Сравнить даты"
          data-testid="sat-strip-action-compare"
          disabled
          className="w-7 h-7 rounded hover:bg-accent flex items-center justify-center text-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeftRight size={14} aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Маска облачности"
          title="Маска облачности"
          aria-pressed={cloudMaskOn}
          data-testid="sat-strip-cloud"
          onClick={() => dispatch({ type: 'TOGGLE_CLOUD_MASK' })}
          className={
            'w-7 h-7 rounded flex items-center justify-center transition-colors ' +
            (cloudMaskOn
              ? 'bg-primary text-primary-foreground'
              : 'hover:bg-accent text-foreground')
          }
        >
          <Cloud size={14} aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Снимок (RGB)"
          title="Снимок (RGB)"
          aria-pressed={rgbSceneOn}
          data-testid="sat-strip-rgb"
          onClick={() => dispatch({ type: 'TOGGLE_RGB_SCENE' })}
          className={
            'w-7 h-7 rounded flex items-center justify-center transition-colors ' +
            (rgbSceneOn
              ? 'bg-primary text-primary-foreground'
              : 'hover:bg-accent text-foreground')
          }
        >
          <Camera size={14} aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Калибровка"
          title="Калибровка"
          data-testid="sat-strip-action-calibration"
          disabled
          className="w-7 h-7 rounded hover:bg-accent flex items-center justify-center text-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Settings size={14} aria-hidden />
        </button>

        {/* Vertical separator */}
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />

        {/* Collapse — fires CLOSE_SAT_PANEL (mute-all). */}
        <button
          type="button"
          aria-label="Скрыть панель"
          title="Скрыть панель (выключит все спутниковые продукты)"
          data-testid="sat-strip-collapse"
          onClick={collapse}
          className="w-7 h-7 rounded hover:bg-accent flex items-center justify-center text-muted-foreground"
        >
          <Minus size={14} aria-hidden />
        </button>
      </div>

      {/* Row 2: year + months + days (ASC) */}
      <div className="flex items-center gap-2 px-2 py-2 text-xs overflow-x-auto">
        {tree.year != null && (
          <div
            className="flex items-center gap-1 text-muted-foreground font-medium shrink-0"
            title="Год сезона. Переключение — в верхнем баре."
          >
            <ChevronLeft size={14} aria-hidden />
            <span>{tree.year}</span>
          </div>
        )}
        {tree.year != null && <span className="text-border shrink-0">|</span>}

        {/* Spec §9.1 row 3 — empty product: message in the date area.
            Distinguishes fetch-in-flight ("загрузка…", key absent) from a
            SETTLED empty result (key present, set empty) — and, per
            AK-604-follow (founder 16.08), the settled-empty case itself now
            splits into three honest reasons instead of one flat "нет
            снимков": still warming up (with an ETA), a plain cloud gap in
            this window, or "taking longer than usual" when we genuinely
            can't tell the first two apart from here. */}
        {sortedMonths.length === 0 && datesLoaded && (
          <span
            className="text-xs text-muted-foreground italic shrink-0"
            data-testid="sat-strip-empty-dates"
            data-empty-reason={emptyDatesReason?.kind ?? undefined}
          >
            {emptyDatesMessage(emptyDatesReason)}
          </span>
        )}
        {!datesLoaded && activeProduct != null && (
          <span
            className="text-xs text-muted-foreground italic shrink-0"
            data-testid="sat-strip-loading-dates"
          >
            загрузка…
          </span>
        )}
        {sortedMonths.map((m) => {
          const active = activeMonth === m;
          return (
            <button
              key={m}
              type="button"
              data-testid={`sat-strip-month-${m + 1}`}
              onClick={() => setActiveMonth(m)}
              className={
                'px-2 py-1 rounded shrink-0 transition-colors ' +
                (active
                  ? 'bg-primary text-primary-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent')
              }
            >
              {MONTHS_RU[m]}
            </button>
          );
        })}

        {sortedMonths.length > 0 && <span className="text-border shrink-0">|</span>}

        {/* Day buttons — ASC (founder bug B): visibleDays comes from
            buildDateTree which sorts ASC. */}
        {visibleDays.map((day) => {
          const dateStr = tree.year != null ? ymd(tree.year, activeMonth!, day) : null;
          const active = dateStr != null && dateStr === selectedDate;
          return (
            <button
              key={day}
              type="button"
              data-testid={`sat-strip-day-${day}`}
              onClick={() => {
                if (dateStr) dispatch({ type: 'SET_SELECTED_DATE', date: dateStr });
              }}
              className={
                'px-2 py-1 rounded shrink-0 font-mono transition-colors ' +
                (active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent')
              }
            >
              {day}
            </button>
          );
        })}

        {/* AK-383 #8: tile-load spinner — instant feedback that the date switch
            registered while COG tiles stream in (CLAUDE.md UX §3: no «толкай и
            жди»). Shown only while a date is selected and its tiles are still
            loading. */}
        {selectedDate && tilesLoading && (
          <span
            className="ml-auto flex items-center gap-1 text-xs text-muted-foreground shrink-0"
            role="status"
            aria-live="polite"
            data-testid="sat-strip-tiles-loading"
          >
            <Loader2 size={14} className="animate-spin text-accent-brand" />
            Загрузка…
          </span>
        )}

        {selectedDate && (
          <button
            type="button"
            onClick={() => dispatch({ type: 'SET_SELECTED_DATE', date: null })}
            className={
              'flex h-6 w-6 items-center justify-center rounded text-muted-foreground shrink-0 hover:bg-accent hover:text-foreground' +
              (tilesLoading ? '' : ' ml-auto')
            }
            aria-label="Сбросить дату"
            data-testid="sat-strip-clear-date"
          >
            {/* Иконка, а не типографский «×» текстом: рядом в том же ряду
                стоят lucide-глифы, и текстовый крестик выпадал из них по
                весу и по базовой линии (design review 18.08, зона 1 · А11). */}
            <X size={14} aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}
